/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 */

import { z } from 'zod';
import { authService } from './auth.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { base64nopad } from '@scure/base';
import { enforceFromRequest } from './api/contracts.js';

type TdFetchOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

/**
 * Single-flight wrapper around `authService.refreshAccessToken()`.
 *
 * Closes #114: without this, two parallel `tdFetch` calls hitting a
 * 401 around the same expiry boundary would each kick off their own
 * `refreshAccessToken` call. When the backend rotates refresh tokens
 * (per F17 / `auth.js:241`), the second call's refresh token is
 * already stale → the call fails → `auth.js`'s `catch` block fires
 * `this.logout()` → the user is bounced to the login page even
 * though the first refresh *succeeded* with a perfectly valid new
 * access token.
 *
 * The module-scoped `refreshInFlight` slot serialises the calls: the
 * first 401 starts the refresh, every concurrent 401 awaits the same
 * promise, and only after it settles do all of them read the new
 * access token via `authService.getAccessToken()` and retry. The
 * `.finally` clears the slot so a *later* 401 (after the new token
 * has its own expiry) starts a fresh refresh round.
 *
 * The slot is reset even on rejection so a transient failure doesn't
 * jam the gate — the next 401 retries from scratch.
 */
let refreshInFlight: Promise<void> | null = null;

const sharedRefresh = (): Promise<void> => {
  if (refreshInFlight === null) {
    refreshInFlight = authService
      .refreshAccessToken()
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

/**
 * Bearer-token-injecting fetch wrapper.
 *
 * For `POST` / `PUT` to `/api/story` and `/api/disclosure`, runs the F15
 * submit-pipeline contract gate (`enforceFromRequest`) *before* calling
 * `fetch`. A plaintext-shaped body, malformed envelope, or schema
 * violation throws `PlaintextLeakError` and the network call never
 * happens. See `src/services/api/contracts.ts`.
 *
 * F17 (#57): on a `401 Unauthorized`, attempts a single transparent
 * token refresh via `authService.refreshAccessToken()` and retries the
 * request once with the new bearer token. On a second 401 — or if the
 * refresh itself fails — the original 401 response is returned to the
 * caller. The retry is deliberately non-recursive to bound the blast
 * radius at exactly one extra round-trip per call.
 *
 * #114: parallel 401s share a single in-flight refresh via
 * `sharedRefresh()` so refresh-token rotation can't cascade into
 * spurious logouts.
 */
export const tdFetch = async (
  url: string,
  options: TdFetchOptions = {}
): Promise<Response> => {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error('No token available');
  }
  // Pre-fetch contract gate. Throws PlaintextLeakError on shape or
  // plaintext-leak failure — the network call is suppressed.
  enforceFromRequest(url, options.method ?? 'GET', options.body);

  const buildInit = (bearerToken: string): RequestInit => ({
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
  });

  const response = await fetch(url, buildInit(token));
  if (response.status !== 401) {
    return response;
  }

  // First 401 — try a single transparent refresh. Any failure (network
  // error, refresh rejected, refresh succeeded but no new token) ends
  // the retry path and returns the original 401 to the caller.
  //
  // #114: `sharedRefresh()` de-duplicates concurrent refresh attempts
  // so a refresh-token rotation can't make a second parallel 401
  // trip over a now-stale refresh token (which would land in
  // `auth.js`'s catch and `logout()` the user mid-flight).
  let refreshed: string | null = null;
  try {
    await sharedRefresh();
    refreshed = authService.getAccessToken();
  } catch {
    return response;
  }
  if (!refreshed || refreshed === token) {
    return response;
  }
  // Single retry with the new bearer. If the retry also returns 401,
  // preserve the *original* 401 Response — matching the JSDoc
  // contract (#115) and avoiding a second auth retry path. This keeps
  // the original unauthorized signal visible to the caller even if the
  // retried request produces a different 401 body or headers. There is
  // no third attempt, no recursion.
  const retry = await fetch(url, buildInit(refreshed));
  return retry.status === 401 ? response : retry;
};

const PublicKeyResponseSchema = z.object({
  keyValue: z.string(),
  keyType: z.string(),
  checksum: z.string(),
});

export const fetchPublicKey = async (url: string): Promise<string> => {
  const res = await tdFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch key: ${res.statusText}`);
  }

  const { keyValue, checksum } = PublicKeyResponseSchema.parse(
    await res.json()
  );

  const keyBytes = base64nopad.decode(keyValue);
  const keyString = new TextDecoder().decode(keyBytes);

  const actualHash = sha256(keyBytes);
  const actualHashBase64 = base64nopad.encode(actualHash);
  const decodedChecksum = new TextDecoder().decode(
    base64nopad.decode(checksum)
  );
  if (actualHashBase64 !== decodedChecksum) {
    throw new Error('Key checksum mismatch');
  }
  return keyString;
};

// -----------------------------------------------------------------------------
// ML-KEM-768 system escrow key (#165 / app-backend#94)
// -----------------------------------------------------------------------------

const ML_KEM_768_KEY_TYPE = 'ml-kem-768';
const ML_KEM_768_PUBLIC_KEY_BYTES = 1184;

const SystemKemPublicKeyResponseSchema = z
  .object({
    keyValue: z.string().min(1),
    keyType: z.literal(ML_KEM_768_KEY_TYPE),
    checksum: z.string().min(1),
  })
  .passthrough();

/**
 * Fetch the system's ML-KEM-768 escrow public key for the vault-passphrase
 * recovery flow.
 *
 * Wire shape (per app-backend#94):
 *   {
 *     "keyValue": "<base64nopad of 1184-byte ML-KEM-768 public key>",
 *     "keyType":  "ml-kem-768",
 *     "checksum": "<base64nopad of SHA-256 of the raw 1184 bytes>"
 *   }
 *
 * Note: this endpoint uses a CLEAN checksum encoding (single base64nopad over
 * the raw hash bytes), distinct from the legacy {@link fetchPublicKey} which
 * double-encodes its checksum. The new endpoint is a clean slate.
 *
 * @param url - endpoint path, defaults to `/api/key/system/escrow-publickey`
 * @returns 1184-byte raw ML-KEM-768 public key
 * @throws on HTTP failure, schema mismatch, wrong key length, or checksum
 *   mismatch
 */
export const fetchSystemKemPublicKey = async (
  url: string = '/api/key/system/escrow-publickey'
): Promise<Uint8Array> => {
  const res = await tdFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch system escrow key: ${res.statusText}`);
  }

  const { keyValue, checksum } = SystemKemPublicKeyResponseSchema.parse(
    await res.json()
  );

  const keyBytes = base64nopad.decode(keyValue);
  if (keyBytes.length !== ML_KEM_768_PUBLIC_KEY_BYTES) {
    throw new Error(
      `System escrow key has wrong length: expected ${ML_KEM_768_PUBLIC_KEY_BYTES}, got ${keyBytes.length}`
    );
  }

  const expectedHash = sha256(keyBytes);
  const providedHash = base64nopad.decode(checksum);
  if (
    providedHash.length !== expectedHash.length ||
    !providedHash.every((b, i) => b === expectedHash[i])
  ) {
    throw new Error('System escrow key checksum mismatch');
  }

  return keyBytes;
};
